import { hasLength, isNullOrUndefined } from '@mtna/core-ts';
import { Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { TabulationDataSet, TabulationDatum, TabulationRecord } from '@rds/rds-base-ts';

import { RdsTabulationDimensionsForm } from '../../dimension-selection';
import { DatumCell, RowDatumCell } from './datum-cell.model';
import { getDatumValue, isDatumUnique, isMatchingDatum } from './datum-util';
import { TableDimension } from './table-dimension.model';
import { TableRow } from './table-row.model';
import { TabulationOptions } from './tabulation-options.model';
import { RdsTabulationSortOptions } from './tabulation-sort-options.model';

export class TabulationTable {
  /** Data Source for the table */
  dataSource: Array<TableRow>;

  columnHeaders: Array<TableDimension> = [];

  /** Whether the options are set to display column subtotals, these are table rows */
  get hasColumnSubtotals(): boolean {
    return !!this.options?.totals?.showColumnSubtotals;
  }

  /** Whether the options are set to display the column totals, i.e. the bottom table row */
  get hasColumnTotals(): boolean {
    return !!this.options?.totals?.showColumnTotals;
  }

  /** Whether the options are set to display the row subtotals, these are table columns */
  get hasRowSubTotals(): boolean {
    return !!this.options?.totals?.showRowSubtotals;
  }

  /** Whether the options are set to display the row totals, i.e. the last table column */
  get hasRowTotals(): boolean {
    return !!this.options?.totals?.showRowTotals;
  }

  tableCellDefs: Array<string> = [];
  tableHeaderDefs: Array<string> = [];

  /** contains each unique column with id and datum */
  columnLookup: Array<DatumCell> = [];
  rowLookup: Array<DatumCell> = [];
  _variableToDatum: { [uri: string]: Array<TabulationDatum> } = {};
  columnTotals: { [columnId: string]: number | null };
  /** ensures 'COUNTS' column remains visible when no column variables were selected */
  // hasNoColumns: boolean;

  /** Keeps track of empty column ids for adjusting TableDimension datumCellSpans */
  private missingColValues: TabulationDatum[][] = [];

  constructor(
    public dataSet: TabulationDataSet,
    public dimensions: RdsTabulationDimensionsForm,
    public options?: TabulationOptions,
    public measureColumnHeader = 'COUNT'
  ) {
    // this.hasNoColumns = !hasLength(dimensions.columns);
    this.dataSource = new Array<TableRow>();

    // Filter unique Record Datum(s)
    //#region TODO - create a 'generateVariableToDatum' function
    const uniqueDatum: Array<TabulationDatum> = [];
    for (const data of this.dataSet.records.map((r) => r.u)) {
      const filteredDatum = data.filter((d) => isDatumUnique(d, uniqueDatum));
      if (!!filteredDatum && !!filteredDatum.length) {
        uniqueDatum.push(...filteredDatum);
      }
    }

    // Map all unique Datum related to the Dimension variable uri
    for (const dim of [...dimensions.rows, ...(dimensions.columns || [])]) {
      if (dim.uri) {
        this._variableToDatum[dim.uri] = uniqueDatum.filter((d) => d.v === dim.uri);
      }
    }
    //#endregion

    // Create row Lookup
    this.recurseLookupItems(dimensions.rows, [], false);
    // Create column Lookup
    if (dimensions.columns?.length) {
      this.recurseLookupItems(dimensions.columns, [], true);
    }

    this.initHeaders();

    this.generateTableRows();
    // RowLookup unused beyond here
    this.rowLookup = [];

    this.columnTotals = this.generateColumnValues(true);
  }

  /**
   * Initializes Column ID definitions & Column TableDimensions
   */
  initHeaders() {
    // Generate ID's for Table Cells & Headers
    this.tableCellDefs = this.generateColumnIds();
    this.tableHeaderDefs = this.generateColumnIds(true);

    if (this.dimensions.columns?.length) {
      // Generate Column TableDimensions
      this.columnHeaders = this.generateTableDimensions(this.dimensions.columns, this.hasRowSubTotals);
      // Push on ID for 'Row Totals' Column
      this.tableCellDefs.push('row-total');
      this.columnHeaders[0].headerIds.push('row-total');
      this.columnHeaders[0].headerIds.unshift('first-row-filler');
    } else {
      this.columnHeaders.push({
        headerIds: [],
        datumCellValues: [],
        name: '',
        nameCellSpan: {},
        variable: null,
        hasMultipleDatum: false,
      });
    }
  }

  /**
   * Returns the Record that matches all the datum
   * @param records the Records to search, either dataSet.records || dataSet.totals
   * @param datumToMatch all the datum that the Record-to-find must contain
   */
  private findRecordOrTotal(records: Array<TabulationRecord>, datumToMatch: TabulationDatum[]): TabulationRecord | undefined {
    return records.find((r) => {
      const data = r.u;
      // Ensure the record holds every datum
      if (data.length === datumToMatch.length && data.every((datum) => datumToMatch.some((d) => isMatchingDatum(d, datum)))) {
        return true;
      }
      return false;
    });
  }

  private generateColumnIds(filterParentSubtotals: boolean = false): Array<string> {
    const filteredItems = filterParentSubtotals
      ? this.columnLookup.filter((item) => !item.subtotal || item.cellspan === 1)
      : this.columnLookup;
    // Making the assumption that all variables have a name
    // If this is ever not the case, we need to fix.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ids = this.dimensions.rows.map((td) => td.name!).concat(filteredItems.map((i) => i.id));
    if (!this.dimensions.columns?.length) {
      ids.push('row-total');
    }
    return ids;
  }

  /**
   * Returns a map of values for each column id
   * @param isSubtotal Determines which DataSet Records to search - records or totals
   * @param allDatum any Row Variable datum that must be included for searching
   */
  private generateColumnValues(isSubtotal = false, allDatum: TabulationDatum[] = []): { [columnId: string]: number | null } {
    const values: { [id: string]: number | null } = {};
    for (const cell of this.columnLookup) {
      const recordsToSearch = !cell.subtotal && !isSubtotal ? this.dataSet.records : this.dataSet.totals;
      const record = this.findRecordOrTotal(recordsToSearch, allDatum.concat(cell.datumStack));
      values[cell.id] = record ? record.d[0].v.o : null;
    }

    const noColumns = !this.dimensions.columns?.length;
    // set value for 'row-total' column
    const recordsToSearch = noColumns && !isSubtotal && allDatum.length ? this.dataSet.records : this.dataSet.totals;
    const total = this.findRecordOrTotal(recordsToSearch, allDatum);
    values['row-total'] = total ? total.d[0].v.o : null;

    return values;
  }

  /**
   * Returns an array of TableDimension.
   * @param tabDims Selected TabulationDimensions - Row or Column
   * @param hasSubtotals If user has included subtotals - Row or Column
   */
  private generateTableDimensions(tabDims: Array<Variable | VariableSummary>, hasSubtotals: boolean): Array<TableDimension> {
    const newDims: Array<TableDimension> = [];
    tabDims.forEach((dim, i) =>
      newDims.push(
        new TableDimension(
          dim,
          dim.uri ? this._variableToDatum[dim.uri] : [],
          newDims[i - 1],
          hasSubtotals,
          this.dataSet.classifications,
          2 * (tabDims.length - newDims.length) - 1
        )
      )
    );

    // Set spans once all TableDimensions are created
    this.calculateCellSpansAndFilterEmptyCells(newDims, hasSubtotals);
    this.missingColValues = [];
    return newDims;
  }

  /**
   * Calculates & sets the span values for datum cells & name cells, deleting any with span of 0.
   * @param dims TableDimensions to set span values
   */
  private calculateCellSpansAndFilterEmptyCells(dims: TableDimension[], hasSubtotals: boolean) {
    let vSpan = 1;
    for (let i = dims.length - 1; i >= 0; i--) {
      const tableDim = dims[i];
      // set the datumCellSpans & build the cumulative vSpan for the next iteration
      vSpan = this.calculateDatumCellSpan(tableDim, i, vSpan, hasSubtotals);
      this.filterEmptyDatumCells(tableDim);
      // set the nameCellSpans of the tableDim
      this.calculateNameCellSpansAndFilterEmptyCells(tableDim, i);
    }
  }

  private filterEmptyDatumCells(dim: TableDimension) {
    dim.datumCellValues = dim.datumCellValues.filter((cell) => cell.cellspan > 0);
  }

  /**
   * Sets the span values for the datumCells of a TableDimension
   * @param dim TableDimension requiring datumCellSpan calculation
   * @param index index of the TableDimension
   * @param vSpan The cumulative span for each iteration of this function
   * @param hasSubtotals Whether the table includes subtotals
   * @returns the recalculated vSpan
   */
  private calculateDatumCellSpan(dim: TableDimension, index: number, vSpan: number, hasSubtotals: boolean): number {
    const variableDatum = dim.variable?.uri ? this._variableToDatum[dim.variable.uri] : [];
    dim.datumCellValues.forEach((cell, cIndex, allCells) => {
      cell.cellspan = cell.subtotal ? cell.cellspan : vSpan;
      // If any empty column datumStack lines up with this cell's datumStack, then we need to reduce its span by
      // the number of matching cells
      const missing = this.missingColValues?.filter((stack) => cell.datumStack.every((d, i) => isMatchingDatum(d, stack[i])));

      if (!!missing && missing.length > 0) {
        if (!cell.subtotal) {
          cell.cellspan = Math.max(vSpan - missing.length, 0);
        } else {
          const prevCells = allCells.filter(
            (c) => c.id !== cell.id && cell.datumStack.every((d) => c.datumStack.some((pd) => isMatchingDatum(pd, d)))
          );
          /** If the previous cell's datum does not match the subtotal cell's last datum, the previous cell is not related to this subtotal
           * and the subtotal should be removed (it's the last remaining column of its parent)
           */
          if (!prevCells.length || prevCells.every((c) => c.cellspan <= 0)) {
            cell.cellspan = 0;
            this.missingColValues?.push(cell.datumStack);
          }
        }
      }
    });
    /**
     * vSpan === the current cells' span
     * multiplied by the number of datum for this TableDimension's variable
     * plus 1 to account for this TableDimension's subtotal column
     */
    return vSpan * variableDatum.length + (dim.hasMultipleDatum && hasSubtotals && index > 0 ? 1 : 0);
  }

  /**
   * Sets the span values for the nameCells of a TableDimension, filtering any with span of 0
   * @param dim TableDimension requiring nameCellSpan calculation
   * @param dimIndex position of the TableDimension in the array of dimensions
   */
  private calculateNameCellSpansAndFilterEmptyCells(dim: TableDimension, dimIndex: number) {
    // When headers have a span of 0, we remove them, so their ids are not defined in the template headerDefs
    const headersToRemove: string[] = [];
    // Each headerId's 'nameCellSpan' needs to equal the sum of all its child datumCellSpans
    for (const headerId of dim.headerIds) {
      // To get the id matching its child datum cells, we need to split off the variable name
      // i.e. '1234sex' should remove 'sex', so that '1234' is the id that datumCells can match against
      const id = headerId.split(dim.variable?.label || dim.variable?.name || '', 2)[0];
      // then we add up all the matching cell spans, and set it to the full headerId
      let span = 0;
      const matching = dim.datumCellValues.filter((cell) => {
        const parentId = cell.datumStack
          .slice(0, dimIndex)
          .map((d) => d.v + getDatumValue(d, [dim.variable], {}))
          .join('');
        return id === parentId;
      });
      span = matching.length ? matching.map((cell) => (cell.subtotal ? 1 : cell.cellspan)).reduce((a, b) => a + b) : span;
      if (span > 0) {
        dim.nameCellSpan[headerId] = span;
      } else {
        headersToRemove.push(headerId);
      }
    }
    // Once we've calculated all the spans, we remove the ones with spans === 0
    dim.headerIds = dim.headerIds.filter((id) => !headersToRemove.some((hId) => hId === id));
  }

  /**
   * Creates an array of TableRows and maps them to the 'dataSource'
   */
  private generateTableRows() {
    // Create a table row for each item in the rowLookup
    let rows = this.rowLookup?.map((datumCell, rowIndex) => {
      const cellCountValues = this.generateColumnValues(datumCell.subtotal, datumCell.datumStack);

      const newRowDatumCells = new Array<RowDatumCell>();
      datumCell.datumStack.forEach((datum, colIndex) => {
        let rowSpan = 1;
        const lastRowIndex = this.dimensions.rows.length - 1;
        let first = true;
        // First row is 'first' for every cell, so only check rowIndex > 0
        if (rowIndex > 0) {
          const previousRow = this.rowLookup[rowIndex - 1];
          const previousRowDatum = previousRow.datumStack[colIndex];
          // if previous row's datum at same colIndex is matching,
          // then we check 1 of 2 things:
          // 1. is colIndex === 0 ? then it's not the first
          // 2. is datum at colIndex - 1 first ? then it's first, else it's not first
          if (isMatchingDatum(previousRowDatum, datum)) {
            first = colIndex === 0 ? false : newRowDatumCells[colIndex - 1].first;
          }
        }

        // Build up the rowspan for the datumCell
        // Start from the last index (innermost row variable column)
        if (first && colIndex < lastRowIndex) {
          let currentIndex = lastRowIndex;
          while (currentIndex > colIndex) {
            const variableUriAtCurrentIndex = this.dimensions.rows[currentIndex].uri;
            const numDatumForVar = variableUriAtCurrentIndex ? this._variableToDatum[variableUriAtCurrentIndex].length : 0;
            rowSpan = rowSpan * numDatumForVar + (this.hasColumnSubtotals && numDatumForVar > 1 ? 1 : 0);
            currentIndex--;
          }
        }

        newRowDatumCells.push(new RowDatumCell(datum, rowSpan, first));
      });
      return new TableRow(newRowDatumCells, cellCountValues, datumCell.subtotal, datumCell.cellspan);
    });
    if (this.options?.filterEmpty) {
      rows = rows.filter(filterEmptyRows);
    }
    this.dataSource = rows;
  }

  /**
   * Generates a LookupItem for each individual table column.
   * Recurse through each column Dimension and create a new LookupItem
   * @param variables The selected Column Dimensions
   * @param prevDatum Array of parent Datum associated with the current Datum
   * @param _iteration DO NOT USE, for internal recursion iteration tracking
   */
  private recurseLookupItems(
    variables: Array<Variable | VariableSummary>,
    prevDatum: Array<TabulationDatum>,
    isColDimension: boolean,
    _iteration = 0
  ) {
    const lookup = isColDimension ? this.columnLookup : this.rowLookup;
    if (variables.length > 0) {
      //#region Row/Column Sorting by name | codeValue, asc | desc
      let variableDatum: TabulationDatum[];
      const firstVariableUri = variables[0].uri;
      const typeProp: keyof RdsTabulationSortOptions = isColDimension ? 'colType' : 'rowType';
      const dirProp: keyof RdsTabulationSortOptions = isColDimension ? 'colDirection' : 'rowDirection';
      if (this.options?.sort[typeProp] !== 'NATURAL') {
        variableDatum = !firstVariableUri
          ? []
          : this._variableToDatum[firstVariableUri].sort((a: TabulationDatum, b: TabulationDatum) => {
              const sort: { type: string; dir: number } = {
                type: this.options?.sort[typeProp] === 'NAME' ? 'name' : 'codeValue',
                dir: this.options?.sort[dirProp] === 'ASC' ? 1 : -1,
              };
              const aValue = getDatumValue(a, variables, this.dataSet.classifications, sort.type) || 0;
              const bValue = getDatumValue(b, variables, this.dataSet.classifications, sort.type) || 0;
              return aValue > bValue ? 1 * sort.dir : -1 * sort.dir;
              //#endregion
            });
      } else {
        variableDatum = !firstVariableUri
          ? []
          : this.options.sort[dirProp] === 'ASC'
          ? this._variableToDatum[firstVariableUri]
          : this._variableToDatum[firstVariableUri].reverse();
      }
      const remainingVariables = variables.slice(1);
      // Loop over each unique Datum and recurse to build columns for each of its 'sub'-datum
      for (const datum of variableDatum) {
        this.recurseLookupItems(remainingVariables, [...prevDatum, datum], isColDimension, _iteration + 1);
      }
      // Create a subtotal LookupItem for each variable with 2+ unique datum
      const hasSubTotals = isColDimension ? this.hasRowSubTotals : this.hasColumnSubtotals;
      if (hasSubTotals && variableDatum.length > 1 && _iteration > 0) {
        let id = '';
        prevDatum.forEach((d) => (id += d.v + getDatumValue(d, variables, this.dataSet.classifications)));
        // if the prevDatum exists in missingColValues for every variableDatum, then don't make subtotal
        if (!variableDatum.every((d) => this.missingColValues.some((stack) => isMatchingDatumStacks(stack, [...prevDatum, d])))) {
          id += 'subtotal';
          const cellspan = Math.max(variables.length, 1);
          lookup.push(new DatumCell(prevDatum, true, id, cellspan));
        }
      }
    } else {
      // current index is the last, innermost variable
      if (
        // Only when the user selects to filter empty columns
        this.options?.filterEmpty &&
        // ensures that we're building the columnLookup
        isColDimension &&
        // If no records exist for the combination of datum, then it's an empty column
        !this.rowLookup.some((d) => this.findRecordOrTotal(this.dataSet.records, [...d.datumStack, ...prevDatum]))
      ) {
        this.missingColValues.push(prevDatum);
      } else {
        // Build a new LookupItem which represents a single column in the table
        const id = prevDatum.map((d) => d.v + getDatumValue(d, variables, this.dataSet.classifications)).join('');
        // There are existing records, so we need to keep it
        lookup.push(new DatumCell(prevDatum, false, id));
      }
    }
  }
}

/**
 * Determines if a TableRow should be removed. If so, modifies all rows affected by removal
 * NOTE: To be used in an 'Array.filter()'
 * @param row TableRow to check
 * @param index index of the TableRow
 * @param rows array of TableRows
 * @example
 * tablesRows.filter(filterEmptyRows)
 */
function filterEmptyRows(row: TableRow, index: number, rows: TableRow[]): row is TableRow {
  // If the countCells { [columnId]: value } only contains null values, then the row should be removed
  if (Object.keys(row.countCells).every((key) => isNullOrUndefined(row.countCells[key]))) {
    // Before removal, we need to modify any table rows that would be affected by the removal
    // We accomplish this by reducing the rowspan of datum at 'dIndex'
    row.datumCells.forEach((d, dIndex) => {
      // If the removed row has datum with first === true,
      // then we modify the next row's datum at dIndex ONLY if the datum.first === false
      if (d.first) {
        const nextRow = rows[index + 1];
        // Only modify if the datum.first === false
        if (!!nextRow && !!nextRow.datumCells[dIndex] && !nextRow.datumCells[dIndex].first) {
          const cell = nextRow.datumCells[dIndex];
          cell.first = d.first;
          cell.rowspan = Math.max(d.rowspan - 1, 1);
        }
      } else {
        // Reverse iterate until we find the row with a datum.first == true at the dIndex & reduce its rowspan
        let rowIndex = index - 1;
        while (rowIndex >= 0) {
          const cell = rows[rowIndex].datumCells[dIndex];
          if (cell.first) {
            cell.rowspan = cell.rowspan > 1 ? cell.rowspan - 1 : cell.rowspan;
            break;
          } else {
            rowIndex--;
          }
        }
      }
    });
    // False is returned, so that the row is filtered out
    return false;
  }
  // The row has countCell values, so we don't filter it
  return true;
}

function isMatchingDatumStacks(s1: TabulationDatum[], s2: TabulationDatum[]): boolean {
  if (!hasLength(s1) || !hasLength(s2) || s1.length !== s2.length) {
    return false;
  }
  return s1.every((d1, index) => isMatchingDatum(s2[index], d1));
}
